/**
 * aiCurriculum.js — canonical AI learning model for My World Quest.
 * (Universal AI Learning System §2 taxonomy, §3 data model, §1 pathway.)
 *
 * This is MWQ's INTERNAL instructional taxonomy — NOT a claim of any
 * country / ministry / regulator curriculum. No such labels appear in the
 * child experience. Only objectives that map to a REAL existing activity
 * are defined here; we do not fabricate objectives to look comprehensive.
 */

// §2 — the 35-category canonical AI knowledge taxonomy (labels only).
export const AI_DOMAINS = [
  "AI Foundations", "Human vs Machine", "Data", "Patterns", "Algorithms",
  "Computational Thinking", "Classification", "Prediction", "Machine Learning",
  "Models", "Model Training", "Model Testing", "Model Evaluation",
  "Generative AI", "Prompting", "Natural Language Processing", "Computer Vision",
  "Robotics", "AI Applications", "Creativity and Innovation", "AI Ethics",
  "Bias and Fairness", "Privacy", "Digital Safety", "Misinformation",
  "AI Errors / Hallucinations", "Copyright", "Plagiarism", "Attribution",
  "Human Oversight", "Responsible AI Use", "AI Projects", "AI Problem Solving",
  "AI Careers", "Human-AI Collaboration",
];

// §1 — canonical age-progressive pathway. `published` gates what a child
// can actually reach; unpublished tiers are shown honestly as "growing".
export const AI_TIERS = [
  { id: "early", name: "AI Explorer", ageBand: "4–5", published: false },
  { id: "discoverer", name: "AI Discoverer", ageBand: "6–7", published: true },
  { id: "builder", name: "AI Builder", ageBand: "8–10", published: true },
  { id: "creator", name: "AI Creator", ageBand: "11–13", published: false },
  { id: "innovator", name: "AI Innovator", ageBand: "14–15", published: false },
];

// §3 — canonical objectives. Each links to a real activity + a mastery
// `skill` key (fed into the existing masteryEngine, no parallel system).
export const AI_OBJECTIVES = [
  {
    id: "ai-examples-intro", tier: "discoverer", aiDomain: "AI Foundations",
    ageBand: "6–7", difficulty: 1,
    learningObjective: "Understand that a machine learns from the examples we give it.",
    skill: "ai.foundations.examples",
    vocabulary: ["example", "teach", "learn"],
    activityType: "train-the-robot", relatedGameIds: ["train-the-robot"],
    prerequisiteIds: [], ethicalDimension: null, safetyDimension: "no personal data",
    teacherReviewStatus: "pending", contentVersion: 1,
  },
  {
    id: "ai-behaviour-change", tier: "discoverer", aiDomain: "Data",
    ageBand: "6–7", difficulty: 1,
    learningObjective: "See that changing the examples changes what the machine does.",
    skill: "ai.foundations.behaviour-change",
    vocabulary: ["change", "sort", "basket"],
    activityType: "train-the-robot", relatedGameIds: ["train-the-robot"],
    prerequisiteIds: ["ai-examples-intro"], ethicalDimension: null, safetyDimension: "no personal data",
    teacherReviewStatus: "pending", contentVersion: 1,
  },
  {
    id: "ai-learns-examples", tier: "builder", aiDomain: "Machine Learning",
    ageBand: "8–10", difficulty: 2,
    learningObjective: "Build a training set and see a model classify new items from it.",
    skill: "ai.ml.learns-from-examples",
    vocabulary: ["training data", "classify", "model"],
    activityType: "pattern-trainer", relatedGameIds: ["pattern-trainer"],
    prerequisiteIds: ["ai-examples-intro"], ethicalDimension: null, safetyDimension: "synthetic data only",
    teacherReviewStatus: "pending", contentVersion: 1,
  },
  {
    id: "ai-data-quality", tier: "builder", aiDomain: "Model Evaluation",
    ageBand: "8–10", difficulty: 2,
    learningObjective: "Notice that more and better examples usually improve accuracy.",
    skill: "ai.data.quality",
    vocabulary: ["accuracy", "examples", "test"],
    activityType: "pattern-trainer", relatedGameIds: ["pattern-trainer"],
    prerequisiteIds: ["ai-learns-examples"], ethicalDimension: null, safetyDimension: "synthetic data only",
    teacherReviewStatus: "pending", contentVersion: 1,
  },
  {
    id: "ai-bias-data", tier: "builder", aiDomain: "Bias and Fairness",
    ageBand: "8–10", difficulty: 3,
    learningObjective: "Explain how unrepresentative examples cause unfair mistakes.",
    skill: "ai.bias.data",
    vocabulary: ["bias", "coverage", "unfair"],
    activityType: "pattern-trainer", relatedGameIds: ["pattern-trainer", "fair-or-unfair"],
    prerequisiteIds: ["ai-data-quality"], ethicalDimension: "fairness", safetyDimension: "synthetic data only",
    teacherReviewStatus: "pending", contentVersion: 1,
  },
  {
    id: "ai-fairness", tier: "discoverer", aiDomain: "AI Ethics",
    ageBand: "6–8", difficulty: 2,
    learningObjective: "Judge whether a machine's decision is fair.",
    skill: "ai.ethics.fairness",
    vocabulary: ["fair", "unfair", "same rule"],
    activityType: "fair-or-unfair", relatedGameIds: ["fair-or-unfair"],
    prerequisiteIds: [], ethicalDimension: "fairness", safetyDimension: null,
    teacherReviewStatus: "pending", contentVersion: 1,
  },
  {
    id: "ai-bias-reason", tier: "builder", aiDomain: "Bias and Fairness",
    ageBand: "8–10", difficulty: 3,
    learningObjective: "Reason about why an AI decision went wrong.",
    skill: "ai.ethics.bias-reason",
    vocabulary: ["cause", "missing", "pattern"],
    activityType: "fair-or-unfair", relatedGameIds: ["fair-or-unfair"],
    prerequisiteIds: ["ai-fairness"], ethicalDimension: "fairness", safetyDimension: null,
    teacherReviewStatus: "pending", contentVersion: 1,
  },
  {
    id: "ai-prompt-clarity", tier: "builder", aiDomain: "Prompting",
    ageBand: "8–10", difficulty: 2,
    learningObjective: "Write a clear instruction saying what, who and how.",
    skill: "ai.prompting.clarity",
    vocabulary: ["prompt", "instruction", "clear"],
    activityType: "prompt-builder", relatedGameIds: ["prompt-builder"],
    prerequisiteIds: [], ethicalDimension: null, safetyDimension: null,
    teacherReviewStatus: "pending", contentVersion: 1,
  },
  {
    id: "ai-safety-privacy", tier: "builder", aiDomain: "Privacy",
    ageBand: "8–10", difficulty: 2,
    learningObjective: "Never share private information with an AI.",
    skill: "ai.safety.privacy",
    vocabulary: ["private", "safe", "personal information"],
    activityType: "prompt-builder", relatedGameIds: ["prompt-builder"],
    prerequisiteIds: [], ethicalDimension: "responsible use", safetyDimension: "privacy",
    teacherReviewStatus: "pending", contentVersion: 1,
  },
  {
    id: "ai-spot-ai", tier: "discoverer", aiDomain: "AI Applications",
    ageBand: "6–8", difficulty: 1,
    learningObjective: "Recognise where AI is used in everyday life — and where it isn't.",
    skill: "ai.foundations.spot",
    vocabulary: ["AI", "learns", "everyday"],
    activityType: "ai-detective", relatedGameIds: ["ai-detective"],
    prerequisiteIds: [], ethicalDimension: null, safetyDimension: null,
    teacherReviewStatus: "pending", contentVersion: 1,
  },
  {
    id: "ai-limits", tier: "builder", aiDomain: "AI Errors / Hallucinations",
    ageBand: "8–10", difficulty: 2,
    learningObjective: "Understand AI's strengths and its limits (it can be wrong).",
    skill: "ai.limits.errors",
    vocabulary: ["mistake", "hallucination", "check"],
    activityType: "human-or-ai", relatedGameIds: ["human-or-ai"],
    prerequisiteIds: ["ai-spot-ai"], ethicalDimension: "responsible use", safetyDimension: null,
    teacherReviewStatus: "pending", contentVersion: 1,
  },
  {
    id: "ai-oversight", tier: "builder", aiDomain: "Human Oversight",
    ageBand: "8–10", difficulty: 2,
    learningObjective: "Decide when to check AI or leave a choice to a person.",
    skill: "ai.responsible.oversight",
    vocabulary: ["human", "check", "decide"],
    activityType: "human-or-ai", relatedGameIds: ["human-or-ai"],
    prerequisiteIds: ["ai-spot-ai"], ethicalDimension: "responsible use", safetyDimension: null,
    teacherReviewStatus: "pending", contentVersion: 1,
  },
];

export const OBJECTIVE_BY_ID = Object.fromEntries(AI_OBJECTIVES.map((o) => [o.id, o]));

export function objectivesForGame(gameId) {
  return AI_OBJECTIVES.filter((o) => o.relatedGameIds.includes(gameId));
}
